import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface TrustedClientsProps {
  t: any;
  isRTL: boolean;
}

const TrustedSection = styled.section`
  padding: 6rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3rem;
  color: #1f2937;
  margin-bottom: 1rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const ClientCard = styled(motion.div)`
  background: white;
  padding: 2.5rem 2rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
    border-color: #dc2626;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 16px;
  }
`;

const ClientLogoContainer = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
  
  @media (max-width: 768px) {
    height: 100px;
  }
`;

const ClientLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.4s ease;
  
  ${ClientCard}:hover & {
    transform: scale(1.05);
  }
`;

const ClientName = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #1f2937;
  margin: 0;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TrustedClients: React.FC<TrustedClientsProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const clients = [
    {
      name: 'Somelec',
      logo: '/images/trust/somelec.png'
    },
    {
      name: 'SNIM',
      logo: '/images/trust/snim.svg'
    },
    {
      name: 'BP',
      logo: '/images/trust/bp.png'
    },
    {
      name: 'Moov Mauritel',
      logo: '/images/trust/moov.png'
    }
  ];

  return (
    <TrustedSection id="trusted-clients" ref={ref}>
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
{t.trustedClientsTitle}
          </SectionTitle>
        </SectionHeader>

        <ClientsGrid>
          {clients.map((client, index) => (
            <ClientCard
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: 0.2 + index * 0.1
              }}
            >
              <ClientLogoContainer>
                <ClientLogo
                  src={client.logo}
                  alt={client.name}
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/logo.png';
                  }}
                />
              </ClientLogoContainer>
              <ClientName $isRTL={isRTL}>{client.name}</ClientName>
            </ClientCard>
          ))}
        </ClientsGrid>
      </Container>
    </TrustedSection>
  );
};

export default TrustedClients;
